import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { HERO_VIDEO, HERO_FALLBACK } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * HomeHero — supports either rotating slideshow or background video, configurable from the admin.
 * Reads from /api/homepage props. Falls back to legacy video if slideshow is empty/disabled.
 */
export default function HomeHero({ hero, slides = [] }) {
  const intervalMs = Math.max(1500, hero?.slideshow_interval_ms || 5000);
  const slideshowOn = hero?.mode === "slideshow" && hero?.slideshow_enabled !== false && slides.length > 0;

  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Preload next slide for smoother fade transitions
  useEffect(() => {
    if (!slideshowOn) return;
    const next = (activeIdx + 1) % slides.length;
    const img = new Image();
    img.src = slides[next]?.image_url;
  }, [activeIdx, slides, slideshowOn]);

  // Auto-rotate
  useEffect(() => {
    if (!slideshowOn || paused) return;
    timerRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [slideshowOn, paused, slides.length, intervalMs]);

  // Respect reduced motion
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) setPaused(true);
  }, []);

  const headlineHtml = hero?.headline_html || "Capturing <em>Timeless</em> Love Stories<br/>Through Photography & Film";

  return (
    <section className="relative min-h-[100vh] w-full overflow-hidden bg-black" data-testid="hero-section">
      {/* Background layer */}
      {slideshowOn ? (
        <div className="absolute inset-0">
          {slides.map((s, i) => (
            <img
              key={s.id || i}
              src={s.image_url}
              alt={s.alt || ""}
              loading={i === 0 ? "eager" : "lazy"}
              data-testid={`hero-slide-${i}`}
              className="absolute inset-0 w-full h-full object-cover ease-in-out"
              style={{ opacity: i === activeIdx ? 1 : 0, transition: "opacity 1500ms ease-in-out" }}
            />
          ))}
        </div>
      ) : (
        <video
          autoPlay loop muted playsInline poster={HERO_FALLBACK}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
      )}

      <div className="absolute inset-0 cine-overlay" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 min-h-[100vh] flex flex-col justify-end pb-24 md:pb-32 pt-32">
        <div className="fade-up">
          <div className="flex items-center gap-3 mb-8">
            <span className="divider-gold" />
            <span className="eyebrow">{hero?.eyebrow || "Wedding Stories Told Honestly"}</span>
          </div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl"
            dangerouslySetInnerHTML={{ __html: headlineHtml.replace(/<em>/g, '<em class="text-[#D4AF37] not-italic">') }} />
          <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed font-light">
            {hero?.subheadline}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            {hero?.primary_cta_label && (
              <Link to={hero.primary_cta_link || "/portfolio"} className="btn-gold" data-testid="hero-portfolio-btn">
                {hero.primary_cta_label} <ArrowRight size={14} />
              </Link>
            )}
            {hero?.secondary_cta_label && (
              <Link to={hero.secondary_cta_link || "/contact#availability"} className="btn-ghost" data-testid="hero-availability-btn">
                {hero.secondary_cta_label}
              </Link>
            )}
            {hero?.tertiary_cta_label && (
              <Link to={hero.tertiary_cta_link || "/contact"} className="btn-ghost" data-testid="hero-quote-btn">
                {hero.tertiary_cta_label}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Slide indicators (only when slideshow active and >1 slide) */}
      {slideshowOn && slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-testid="hero-slide-indicators">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setActiveIdx(i)} aria-label={`Go to slide ${i + 1}`}
              className={`h-1 transition-all ${i === activeIdx ? "w-10 bg-[#D4AF37]" : "w-5 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      )}

      <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-white/60 text-xs tracking-widest uppercase">
        <span className="h-px w-10 bg-[#D4AF37]" />Scroll to discover
      </div>
    </section>
  );
}
